import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { ArrowUpCircle, ArrowDownCircle, RefreshCw, Clock, XCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Shared wallet transactions panel.
 * Usage: <WalletTransactions filterStudentId={studentId?} showReconcile={true} />
 * - Parents & students see their own scope (backend enforces).
 * - Parents get a "Reconcile" button that queries Paymob for stuck pendings and settles them.
 */
export function WalletTransactions({ filterStudentId = null, showReconcile = true }) {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reconciling, setReconciling] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/canteen/wallet/my/transactions?limit=50");
      const scoped = filterStudentId
        ? (data || []).filter(x => x.student_id === filterStudentId)
        : (data || []);
      setItems(scoped);
    } catch (e) {
      // silent — showing empty list is fine
    }
    setLoading(false);
  };

  // Self-heal on mount: if there are stuck Paymob pendings, ask backend to reconcile with Paymob
  const reconcile = async (silent = false) => {
    setReconciling(true);
    try {
      const { data } = await api.post("/canteen/wallet/topup/paymob/reconcile");
      if (!silent && data?.reconciled > 0) {
        toast.success(`${t("reconciled_prefix") || "تم تأكيد"} ${data.reconciled} ${t("reconciled_suffix") || "عملية شحن"}`);
      }
      if (data?.reconciled > 0) await load();
    } catch (e) {
      if (!silent) toast.error(e.response?.data?.detail || "Reconcile failed");
    }
    setReconciling(false);
  };

  useEffect(() => {
    (async () => {
      if (showReconcile) await reconcile(true);
      await load();
    })();
  }, [filterStudentId]);

  return (
    <Card className="p-4 border shadow-none" data-testid="wallet-transactions">
      <div className="flex items-center justify-between mb-3 gap-2">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <ArrowUpCircle className="w-5 h-5 text-primary" />
          {t("my_transactions") || "معاملاتي"}
        </h3>
        {showReconcile && (
          <Button variant="outline" size="sm" onClick={() => reconcile(false)} disabled={reconciling}
                  data-testid="reconcile-btn">
            <RefreshCw className={`w-3.5 h-3.5 me-1.5 ${reconciling ? "animate-spin" : ""}`} />
            {t("reconcile") || "تحديث الحالة"}
          </Button>
        )}
      </div>

      {loading ? (
        <div className="py-8 text-center text-sm text-muted-foreground">{t("loading")}</div>
      ) : items.length === 0 ? (
        <div className="py-8 text-center text-sm text-muted-foreground">{t("no_transactions") || "لا توجد معاملات بعد"}</div>
      ) : (
        <div className="divide-y divide-border">
          {items.map(it => (
            <TransactionRow key={it.id} it={it} t={t} />
          ))}
        </div>
      )}
    </Card>
  );
}

function TransactionRow({ it, t }) {
  const isTopup = it.type === "topup";
  const isSale = it.type === "sale";
  const isRefund = it.type === "refund";
  const Icon = isTopup ? ArrowUpCircle : isSale ? ArrowDownCircle : ArrowUpCircle;
  const iconColor = it.status === "failed" ? "text-destructive"
                  : isTopup ? "text-emerald-600"
                  : isSale ? "text-amber-600"
                  : "text-sky-600";
  const StatusIcon = it.status === "success" ? CheckCircle2 : it.status === "pending" ? Clock : XCircle;
  const statusColor = it.status === "success" ? "text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40"
                    : it.status === "pending" ? "text-amber-600 bg-amber-50 dark:bg-amber-950/40"
                    : "text-destructive bg-destructive/10";
  const label = isTopup ? (t("topup") || "شحن")
              : isSale ? (t("sale") || "شراء")
              : isRefund ? (t("refund") || "استرداد")
              : (t("adjustment") || "تعديل");
  const sign = (isSale) ? "-" : (isRefund || isTopup) ? "+" : "";

  return (
    <div className="py-3 flex items-center gap-3" data-testid={`txn-${it.id}`}>
      <Icon className={`w-8 h-8 shrink-0 ${iconColor}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm">{label}</span>
          {it.student_name && (
            <span className="text-xs text-muted-foreground truncate">— {it.student_name}</span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5 text-xs">
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${statusColor}`}>
            <StatusIcon className="w-3 h-3" />
            {t(`txn_status_${it.status}`) || it.status}
          </span>
          <span className="text-muted-foreground font-mono">{(it.at || "").slice(0, 16).replace("T", " ")}</span>
          {it.gateway && (
            <span className="text-muted-foreground uppercase tracking-wide text-[10px]">{it.gateway}</span>
          )}
        </div>
      </div>
      <div className={`font-mono font-semibold text-sm ${sign === "-" ? "text-destructive" : sign === "+" ? "text-emerald-600" : ""}`}>
        {sign}{Number(it.amount).toFixed(2)}
      </div>
    </div>
  );
}
